import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import {
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Stack,
  Typography,
  Slide,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "BASE_URL";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button } from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Close as CloseIcon,
  Sell as SellIcon,
  ShoppingCart as ShoppingCartIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";

import ChatIcon from "@mui/icons-material/Chat";
import { useNavigate } from "react-router-dom";

function UserDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // 👈 new state for search

  const metaRed = "#C62828";

  // Calculate post statistics
  const calculatePostStats = (posts) => {
    if (!posts || posts.length === 0) {
      return {
        total: 0,
        active: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        sellPosts: 0,
        buyPosts: 0,
      };
    }

    const stats = {
      total: posts.length,
      active: posts.filter((p) => p.status === "active").length,
      pending: posts.filter((p) => p.status === "pending").length,
      approved: posts.filter((p) => p.is_admin_approved === true).length,
      rejected: posts.filter((p) => p.is_admin_approved === false && p.status === "rejected")
        .length,
      sellPosts: posts.filter((p) => p.post_type === "sell").length,
      buyPosts: posts.filter((p) => p.post_type === "buy").length,
    };

    return stats;
  };

  const postStats = calculatePostStats(user?.posts);

  // 👇 Filter posts based on search query (title or last 5 digits of post ID)
  const getFilteredPosts = () => {
    if (!user?.posts) return [];
    if (!searchQuery.trim()) return user.posts;

    const query = searchQuery.trim().toLowerCase();
    console.log(query);
    return user.posts.filter((post) => {
      const titleMatch = post.title?.toLowerCase().includes(query);
      const last5Id = post._id?.slice(-5).toLowerCase();
      const idMatch = last5Id === query; // exact match on last 5 chars
      return titleMatch || idMatch;
    });
  };

  const filteredPosts = getFilteredPosts();

  // Card Styles
  const cardStyle = {
    borderRadius: "20px",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 12px 20px rgba(0,0,0,0.2)",
    },
  };

  const statCardStyle = {
    borderRadius: "15px",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: "0 8px 15px rgba(0,0,0,0.1)",
    },
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/admin/userdetails?id=${id}`);
        if (response.data.success) {
          setUser(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  const togglePostExpand = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress color="error" />
      </MDBox>
    );
  }

  if (!user) return <MDTypography>User not found</MDTypography>;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        mt={4}
        mb={3}
        sx={{
          animation: "fadeIn 0.8s ease-in-out",
          "@keyframes fadeIn": {
            "0%": { opacity: 0, transform: "translateY(10px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Grid container spacing={3}>
          {/* LEFT COLUMN: Profile Header */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: "24px",
                position: "relative",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.8)",
                transition: "all 0.4s ease",
                height: "100%",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                },
              }}
            >
              {/* Premium Top Cover/Banner Background */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 120,
                  background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                  opacity: 0.8,
                  zIndex: 0,
                }}
              />
              {/* Decorative Curve */}
              <Box
                sx={{
                  position: "absolute",
                  top: 80,
                  left: -50,
                  right: -50,
                  height: 100,
                  background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                  borderRadius: "50%",
                  zIndex: 0,
                }}
              />

              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                pt={5}
                position="relative"
                zIndex={1}
              >
                {/* Premium Avatar with Glowing Pulse */}
                <MDBox
                  sx={{
                    position: "relative",
                    p: 0.5,
                    borderRadius: "50%",
                    mb: 2,
                    background: "#fff",
                    animation: "pulsePremium 2.5s infinite",
                    "@keyframes pulsePremium": {
                      "0%": { boxShadow: "0 0 0 0 rgba(26, 115, 232, 0.4)" },
                      "70%": { boxShadow: "0 0 0 15px rgba(26, 115, 232, 0)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(26, 115, 232, 0)" },
                    },
                  }}
                >
                  <Avatar
                    src={user?.profile_photo}
                    sx={{
                      width: 120,
                      height: 120,
                      border: "4px solid #fff",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                      bgcolor: "#f0f3f8",
                    }}
                  />
                  {user?.is_verified && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 5,
                        right: 5,
                        bgcolor: "#fff",
                        borderRadius: "50%",
                        p: 0.5,
                        display: "flex",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      }}
                    >
                      <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 24 }} />
                    </Box>
                  )}
                </MDBox>

                <MDTypography
                  variant="h4"
                  fontWeight="900"
                  color="text.primary"
                  sx={{ letterSpacing: "-0.5px" }}
                >
                  {user?.name || "N/A"}
                </MDTypography>
                <MDTypography
                  variant="button"
                  color="primary"
                  fontWeight="bold"
                  mb={2}
                  sx={{ letterSpacing: "0.5px" }}
                >
                  {user?.company_name || "N/A"}
                </MDTypography>

                {/* Glowing Badges */}
                <MDBox display="flex" gap={1.5} mb={2}>
                  <Chip
                    icon={
                      user?.is_verified ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <CancelIcon fontSize="small" />
                      )
                    }
                    label={user?.is_verified ? "Verified User" : "Unverified"}
                    sx={{
                      bgcolor: user?.is_verified ? "#E8F5E9" : "#FFEBEE",
                      color: user?.is_verified ? "#2E7D32" : "#C62828",
                      fontWeight: "bold",
                      borderRadius: "10px",
                      boxShadow: user?.is_verified
                        ? "0 4px 10px rgba(76, 175, 80, 0.2)"
                        : "0 4px 10px rgba(198, 40, 40, 0.2)",
                      border: `1px solid ${
                        user?.is_verified ? "rgba(76, 175, 80, 0.3)" : "rgba(198, 40, 40, 0.3)"
                      }`,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                  <Chip
                    label={user?.status?.toUpperCase() || "N/A"}
                    sx={{
                      bgcolor: "#f8f9fa",
                      color: "#333",
                      fontWeight: "bold",
                      borderRadius: "10px",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    }}
                  />
                </MDBox>
              </MDBox>

              <Divider sx={{ my: 2, borderStyle: "dashed" }} />

              <MDBox textAlign="left" px={3} pb={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ letterSpacing: "1.5px" }}
                  mb={2}
                  display="block"
                >
                  CONTACT INFORMATION
                </MDTypography>

                {/* Modern Contact Rows replacing old DetailRow */}
                <Stack spacing={1.5}>
                  {[
                    {
                      icon: <EmailIcon fontSize="small" />,
                      label: "Email",
                      value: user?.email_id || "N/A",
                    },
                    {
                      icon: <PhoneIcon fontSize="small" />,
                      label: "Mobile",
                      value: user?.mobile_number || "N/A",
                    },
                    {
                      icon: <ChatIcon fontSize="small" />,
                      label: "WhatsApp",
                      value: user?.whatsapp_number || "N/A",
                    },
                    {
                      icon: <LocationOnIcon fontSize="small" />,
                      label: "Location",
                      value: `${user?.city || ""}${user?.city && user?.state ? ", " : ""}${
                        user?.state || "N/A"
                      }`,
                    },
                    {
                      icon: <BusinessIcon fontSize="small" />,
                      label: "GST Number",
                      value: user?.gst_number || "N/A",
                    },
                    {
                      icon: <CategoryIcon fontSize="small" />,
                      label: "Business Type",
                      value: user?.business_type || "N/A",
                    },
                  ].map((item, index) => (
                    <MDBox
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        bgcolor: "rgba(255, 255, 255, 0.6)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.9)",
                        borderRadius: "14px",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateX(6px)",
                          bgcolor: "rgba(255, 255, 255, 1)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          background: "linear-gradient(135deg, #ffffff 0%, #f0f3f8 100%)",
                          boxShadow:
                            "inset 0 2px 4px rgba(255,255,255,1), 0 2px 5px rgba(0,0,0,0.05)",
                          borderRadius: "10px",
                          color: "#1A73E8",
                          mr: 2,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ flex: 1, overflow: "hidden" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                          display="block"
                          sx={{ mb: -0.2 }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="h6" fontWeight="medium" color="text.primary" noWrap>
                          {item.value}
                        </Typography>
                      </Box>
                    </MDBox>
                  ))}
                </Stack>
              </MDBox>
            </Card>
          </Grid>

          {/* ================= MIDDLE/MAIN COLUMN (Stats & Posts) ================= */}
          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              {/* 1. Premium Active Subscription Plan */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                    color: "white",
                    borderRadius: "20px", // Thoda compact border radius
                    p: 3,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -50,
                      right: -30,
                      width: 250,
                      height: 250,
                      background:
                        "radial-gradient(circle, rgba(26,115,232,0.3) 0%, rgba(0,0,0,0) 70%)",
                      borderRadius: "50%",
                      zIndex: 0,
                    }}
                  />

                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    position="relative"
                    zIndex={1}
                    mb={2.5}
                  >
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="white"
                        sx={{ opacity: 0.7, letterSpacing: "1px", fontWeight: "bold" }}
                      >
                        CURRENT PLAN
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="900" sx={{ mt: 0.5 }}>
                        {user?.subscriptionPlan?.name || "No Active Plan"}
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" mt={0.5}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "#4ade80",
                            borderRadius: "50%",
                            mr: 1,
                            boxShadow: "0 0 10px #4ade80",
                          }}
                        />
                        <MDTypography
                          variant="button"
                          color="white"
                          sx={{ fontWeight: "bold", opacity: 0.9 }}
                        >
                          {user?.userMembershipPlan?.status?.toUpperCase() || "INACTIVE"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Box
                      sx={{
                        p: 1.5, // Padding reduce ki
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.1)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      {/* Icon Size Reduce Kiya (2rem) */}
                      <Icon sx={{ fontSize: "2rem !important", color: "#60a5fa" }}>
                        workspace_premium
                      </Icon>
                    </Box>
                  </MDBox>

                  <Grid container spacing={2} position="relative" zIndex={1}>
                    {[
                      {
                        label: "Posts Used",
                        value: `${user?.userMembershipPlan?.postUsed || 0} / ${
                          user?.subscriptionPlan?.post_limit || 0
                        }`,
                      },
                      {
                        label: "Views Used",
                        value: `${user?.userMembershipPlan?.viewUsed || 0} / ${
                          user?.subscriptionPlan?.view_limit || 0
                        }`,
                      },
                      {
                        label: "Validity",
                        value: `${user?.subscriptionPlan?.duration_days || 0} Days`,
                      },
                      {
                        label: "Expiry Date",
                        value: user?.userMembershipPlan?.endDate
                          ? new Date(user?.userMembershipPlan?.endDate).toLocaleDateString("en-GB")
                          : "N/A",
                      },
                    ].map((stat, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box
                          sx={{
                            background: "rgba(255,255,255,0.05)",
                            p: 1.5,
                            borderRadius: "12px",
                            border: "1px solid rgba(255,255,255,0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#94a3b8", display: "block", mb: 0.5, fontWeight: "bold" }}
                          >
                            {stat.label}
                          </MDTypography>
                          <MDTypography variant="button" color="white" fontWeight="bold">
                            {stat.value}
                          </MDTypography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>

              {/* Extra Plan Details (If any) */}
              {user?.extraPlan && (
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                      p: 2.5,
                      borderRadius: "16px",
                      borderLeft: `5px solid #1A73E8`,
                      boxShadow: "0 5px 15px rgba(0,0,0,0.04)",
                    }}
                  >
                    <MDTypography variant="h6" fontWeight="900" mb={1.5}>
                      Extra Plan
                    </MDTypography>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      mb={1}
                      p={1}
                      bgcolor="#fff"
                      borderRadius="8px"
                      border="1px solid #eee"
                    >
                      <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
                        Plan Name
                      </MDTypography>
                      <MDTypography variant="button" fontWeight="bold" color="primary">
                        {user?.extraPlan?.name}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      p={1}
                      bgcolor="#fff"
                      borderRadius="8px"
                      border="1px solid #eee"
                    >
                      <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
                        Remaining Posts
                      </MDTypography>
                      <MDTypography variant="button" color="error" fontWeight="bold">
                        {user?.userExtraPlan?.postRemaining}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* Address Card */}
              <Grid item xs={12} md={user?.extraPlan ? 6 : 12}>
                <Card
                  sx={{
                    background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                    p: 2.5,
                    height: "100%",
                    borderRadius: "16px",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.04)",
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <LocationOnIcon sx={{ color: "#1A73E8", mr: 1 }} fontSize="small" />
                    <MDTypography variant="h6" fontWeight="bold">
                      Address
                    </MDTypography>
                  </MDBox>
                  <Divider sx={{ my: 1 }} />
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {user?.address || "No address provided for this user."}
                  </MDTypography>
                </Card>
              </Grid>

              {/* 2. COMPACT POST STATISTICS SECTION */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: "20px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.03)",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <MDBox mb={2} display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)",
                        borderRadius: "12px",
                        color: "#fff",
                        mr: 2,
                        boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                      }}
                    >
                      <Icon fontSize="small">analytics</Icon>
                    </Box>
                    <MDTypography variant="h5" fontWeight="bold" color="text.primary">
                      Posts Overview
                    </MDTypography>
                  </MDBox>

                  <Grid container spacing={2}>
                    {/* Compact Stat Cards (Smaller Size) */}
                    {[
                      {
                        title: "Total",
                        value: postStats?.total || 0,
                        icon: "format_list_bulleted",
                        gradient: "linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)",
                        shadow: "rgba(142, 84, 233, 0.2)",
                      },
                      {
                        title: "Active",
                        value: postStats?.active || 0,
                        icon: "check_circle",
                        gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                        shadow: "rgba(56, 239, 125, 0.2)",
                      },
                      {
                        title: "Pending",
                        value: postStats?.pending || 0,
                        icon: "pending",
                        gradient: "linear-gradient(135deg, #FF8008 0%, #FFA081 100%)",
                        shadow: "rgba(255, 128, 8, 0.2)",
                      },
                      {
                        title: "Approved",
                        value: postStats?.approved || 0,
                        icon: "verified",
                        gradient: "linear-gradient(135deg, #00B4DB 0%, #0083B0 100%)",
                        shadow: "rgba(0, 180, 219, 0.2)",
                      },
                    ].map((stat, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box
                          sx={{
                            background: stat.gradient,
                            color: "#fff",
                            p: 1.5,
                            borderRadius: "14px",
                            textAlign: "center",
                            boxShadow: `0 4px 10px ${stat.shadow}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-3px)",
                              boxShadow: `0 8px 15px ${stat.shadow}`,
                            },
                          }}
                        >
                          <Icon sx={{ fontSize: "1.8rem !important", mb: 0.5, opacity: 0.9 }}>
                            {stat.icon}
                          </Icon>
                          <MDTypography variant="h4" fontWeight="bold" color="white">
                            {stat.value}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="white"
                            sx={{ fontWeight: "medium", display: "block" }}
                          >
                            {stat.title}
                          </MDTypography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          bgcolor: "#fff",
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid rgba(255, 65, 108, 0.2)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
                            SELL POSTS
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="bold" color="error">
                            {postStats?.sellPosts || 0}
                          </MDTypography>
                        </Box>
                        <SellIcon sx={{ color: "#FF416C", opacity: 0.7 }} />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          bgcolor: "#fff",
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid rgba(26, 115, 232, 0.2)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
                            BUY POSTS
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="bold" color="info">
                            {postStats?.buyPosts || 0}
                          </MDTypography>
                        </Box>
                        <ShoppingCartIcon sx={{ color: "#1A73E8", opacity: 0.7 }} />
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* 3. RECENT POSTS LIST (WITH PROPER UI & COLORFUL EXPANDED VIEW) */}
              {user?.posts && user.posts.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    sx={{ p: 3, borderRadius: "20px", boxShadow: "0 10px 25px rgba(0,0,0,0.03)" }}
                  >
                    <MDBox mb={2.5} display="flex" alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                          borderRadius: "12px",
                          color: "#fff",
                          mr: 2,
                          boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                        }}
                      >
                        <Icon fontSize="small">list_alt</Icon>
                      </Box>
                      <MDTypography variant="h5" fontWeight="bold" color="text.primary">
                        Recent Posts
                      </MDTypography>
                    </MDBox>

                    {/* Compact Search Bar */}
                    <MDBox mb={3}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          bgcolor: "#f8f9fa",
                          borderRadius: "12px",
                          border: "1px solid #e0e0e0",
                          p: "4px 12px",
                          transition: "0.3s",
                          "&:focus-within": {
                            borderColor: "#1A73E8",
                            bgcolor: "#fff",
                            boxShadow: "0 0 0 3px rgba(26,115,232,0.1)",
                          },
                        }}
                      >
                        <Icon sx={{ color: "#888", mr: 1 }} fontSize="small">
                          search
                        </Icon>
                        <input
                          type="text"
                          placeholder="Search post title or ID..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            width: "100%",
                            padding: "8px 0",
                            fontSize: "0.9rem",
                            background: "transparent",
                          }}
                        />
                        {searchQuery && (
                          <IconButton size="small" onClick={() => setSearchQuery("")}>
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        )}
                      </Box>
                    </MDBox>

                    {/* Posts List */}
                    <Grid container spacing={2}>
                      {filteredPosts.length === 0 ? (
                        <Grid item xs={12}>
                          <MDBox
                            textAlign="center"
                            py={4}
                            bgcolor="#f8f9fa"
                            borderRadius="12px"
                            border="1px dashed #ccc"
                          >
                            <Icon sx={{ fontSize: "3rem", color: "#ccc", mb: 1 }}>search_off</Icon>
                            <MDTypography
                              variant="button"
                              fontWeight="bold"
                              color="text.secondary"
                              display="block"
                            >
                              No posts found
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      ) : (
                        filteredPosts.map((post, index) => (
                          <Grid item xs={12} key={post._id}>
                            <Slide direction="up" in={true} timeout={300 + index * 100}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: "12px",
                                  border: "1px solid #eee",
                                  bgcolor: expandedPost === post._id ? "#fafcff" : "#fff",
                                  boxShadow:
                                    expandedPost === post._id
                                      ? "0 8px 20px rgba(26, 115, 232, 0.08)"
                                      : "0 2px 5px rgba(0,0,0,0.02)",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform:
                                      expandedPost === post._id ? "none" : "translateY(-2px)",
                                    borderColor: "#1A73E8",
                                  },
                                }}
                                onClick={() => togglePostExpand(post._id)}
                              >
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  gap={1.5}
                                >
                                  {/* Title & PROPER ID BADGE */}
                                  <MDBox display="flex" alignItems="center" gap={2}>
                                    <Box
                                      sx={{
                                        p: 1,
                                        borderRadius: "8px",
                                        background:
                                          post.post_type === "sell"
                                            ? "rgba(255, 65, 108, 0.1)"
                                            : "rgba(26, 115, 232, 0.1)",
                                        color: post.post_type === "sell" ? "#FF416C" : "#1A73E8",
                                        display: "flex",
                                      }}
                                    >
                                      {post.post_type === "sell" ? (
                                        <SellIcon fontSize="small" />
                                      ) : (
                                        <ShoppingCartIcon fontSize="small" />
                                      )}
                                    </Box>
                                    <Box>
                                      <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                          color="text.primary"
                                          sx={{ lineHeight: 1 }}
                                        >
                                          {post.title}
                                        </MDTypography>
                                        <Chip
                                          label={`ID: ${post._id.slice(-5).toUpperCase()}`}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: "0.65rem",
                                            fontWeight: "bold",
                                            bgcolor: "#e2e8f0",
                                            color: "#475569",
                                          }}
                                        />
                                      </MDBox>
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="medium"
                                        color="text.secondary"
                                      >
                                        {new Date(post.createdAt).toLocaleDateString("en-GB")}
                                      </MDTypography>
                                    </Box>
                                  </MDBox>

                                  <MDBox display="flex" gap={1} alignItems="center">
                                    <Chip
                                      label={post.post_type?.toUpperCase()}
                                      size="small"
                                      sx={{
                                        bgcolor: post.post_type === "sell" ? "#FF416C" : "#1A73E8",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        height: 22,
                                        fontSize: "0.65rem",
                                      }}
                                    />
                                    <Chip
                                      label={post.status?.toUpperCase()}
                                      size="small"
                                      color={post.status === "active" ? "success" : "warning"}
                                      sx={{
                                        fontWeight: "bold",
                                        height: 22,
                                        fontSize: "0.65rem",
                                        color: "#fff",
                                      }}
                                    />
                                    <Icon
                                      color="action"
                                      sx={{
                                        transition: "0.3s",
                                        transform:
                                          expandedPost === post._id
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                      }}
                                    >
                                      expand_more
                                    </Icon>
                                  </MDBox>
                                </MDBox>

                                {/* COLORFUL EXPANDED DETAILS (No White-White) */}
                                <Collapse in={expandedPost === post._id}>
                                  <MDBox mt={2} pt={2} borderTop="1px dashed #ddd">
                                    <Grid container spacing={2}>
                                      {/* Price & Quantity Blocks (Soft Tinted Backgrounds) */}
                                      <Grid item xs={6} sm={4}>
                                        <Box
                                          p={1.5}
                                          bgcolor="rgba(26, 115, 232, 0.06)"
                                          borderRadius="8px"
                                          border="1px solid rgba(26, 115, 232, 0.1)"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="info"
                                            fontWeight="bold"
                                            display="block"
                                          >
                                            PRICE
                                          </MDTypography>
                                          <MDTypography
                                            variant="h6"
                                            color="text.primary"
                                            fontWeight="bold"
                                          >
                                            ₹{post.price}
                                            <span style={{ fontSize: "0.8rem", color: "#666" }}>
                                              &nbsp;/ {post.unitType}
                                            </span>
                                          </MDTypography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={6} sm={4}>
                                        <Box
                                          p={1.5}
                                          bgcolor="rgba(255, 152, 0, 0.06)"
                                          borderRadius="8px"
                                          border="1px solid rgba(255, 152, 0, 0.1)"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="warning"
                                            fontWeight="bold"
                                            display="block"
                                          >
                                            QUANTITY
                                          </MDTypography>
                                          <MDTypography
                                            variant="h6"
                                            color="text.primary"
                                            fontWeight="bold"
                                          >
                                            {post.quantity}
                                            <span style={{ fontSize: "0.8rem", color: "#666" }}>
                                              &nbsp;/ {post.unitType}
                                            </span>
                                          </MDTypography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Box
                                          p={1.5}
                                          bgcolor="rgba(76, 175, 80, 0.06)"
                                          borderRadius="8px"
                                          border="1px solid rgba(76, 175, 80, 0.1)"
                                          height="100%"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="success"
                                            fontWeight="bold"
                                            display="block"
                                          >
                                            CATEGORY
                                          </MDTypography>
                                          <MDTypography
                                            variant="button"
                                            color="text.primary"
                                            fontWeight="bold"
                                          >
                                            {post.childCategory?.name || "N/A"}
                                          </MDTypography>
                                        </Box>
                                      </Grid>

                                      {/* Location Row */}
                                      <Grid item xs={12}>
                                        <Box
                                          p={1.5}
                                          bgcolor="rgba(156, 39, 176, 0.04)"
                                          borderRadius="8px"
                                          border="1px solid rgba(156, 39, 176, 0.1)"
                                          display="flex"
                                          alignItems="center"
                                        >
                                          <LocationOnIcon
                                            sx={{ color: "#9c27b0", mr: 1, fontSize: "1.2rem" }}
                                          />
                                          <Box>
                                            <MDTypography
                                              variant="caption"
                                              sx={{ color: "#9c27b0" }}
                                              fontWeight="bold"
                                              display="block"
                                              lineHeight={1}
                                            >
                                              LOCATION
                                            </MDTypography>
                                            <MDTypography
                                              variant="button"
                                              color="text.primary"
                                              fontWeight="bold"
                                            >
                                              {post?.location || "N/A"}
                                            </MDTypography>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      {/* Description Row */}
                                      {/* <Grid item xs={12}>
                                        <Box
                                          p={1.5}
                                          bgcolor="#f8fafc"
                                          borderRadius="8px"
                                          border="1px solid #e2e8f0"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="bold"
                                            display="block"
                                            mb={0.5}
                                          >
                                            DESCRIPTION
                                          </MDTypography>
                                          <MDTypography
                                            variant="body2"
                                            color="text.primary"
                                            fontWeight="medium"
                                            sx={{ whiteSpace: "pre-wrap" }}
                                          >
                                            {post.description || "No description provided."}
                                          </MDTypography>
                                        </Box>
                                      </Grid> */}

                                      {/* View Post Button - Added Here */}
                                      <Grid item xs={12}>
                                        <Button
                                          variant="contained"
                                          fullWidth
                                          onClick={() => navigate(`/post-details/${post._id}`)}
                                          sx={{
                                            bgcolor: "#1A73E8",
                                            "&:hover": {
                                              bgcolor: "#0D47A1",
                                            },
                                            py: 1,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontWeight: "bold",
                                            color: "#fff",
                                          }}
                                          startIcon={<VisibilityIcon />}
                                        >
                                          View Full Post Details
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </MDBox>
                                </Collapse>
                              </Card>
                            </Slide>
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

// Helper Components (same as before)
function DetailRow({ icon, label, value }) {
  return (
    <MDBox
      display="flex"
      alignItems="center"
      mb={1.5}
      sx={{ "&:hover icon": { transform: "scale(1.2)" } }}
    >
      <Icon sx={{ mr: 1.5, color: "#C62828", fontSize: "1.2rem", transition: "0.2s" }}>{icon}</Icon>
      <MDBox>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          display="block"
          sx={{ lineHeight: 1 }}
        >
          {label}
        </MDTypography>
        <MDTypography variant="button" fontWeight="medium">
          {value || "N/A"}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

function StatBox({ label, value }) {
  return (
    <Grid item xs={6} md={3}>
      <MDBox
        sx={{
          bgcolor: "rgba(255,255,255,0.1)",
          p: 1.5,
          borderRadius: "10px",
          textAlign: "center",
          transition: "0.3s",
          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
        }}
      >
        <MDTypography variant="caption" color="white" opacity={0.8} display="block">
          {label}
        </MDTypography>
        <MDTypography variant="h6" color="white" fontWeight="bold">
          {value}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

DetailRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StatBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default UserDetails;
